import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Checkbox, CircularProgress, Divider, FormControlLabel, Paper, Typography } from '@mui/material';
import { isEmpty, pickBy } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import type { Company } from 'api';
import { allocationsAction, captableAction } from 'common/actions';
import { companySummaryUrl, firmSummaryUrl } from 'common/config/urls';
import { AI_GRADIENT_COLORS, AI_GRADIENT_CSS } from 'common/constants/ai';
import {
  COMPANIES_TOOLTIP_TEXT_COMPANY_MEASUREMENT_DATE,
  COMPANY_TYPE,
  RELATED_ENTITY,
} from 'common/constants/companies';
import { useStore } from 'common/store';
import { FormDialog } from 'components';
import CommonProfilePicker from 'components/CommonProfilePicker';
import { ConfirmationDelete, ConfirmationDialog } from 'components/Dialogs';
import { CompanyForm } from 'components/Forms';
import { CompanyFormDefaultFormState } from 'components/Forms/CompanyForm/CompanyForm.props';
import DialogContext from 'context/DialogContext';
import {
  useConfirmCompanyProfile,
  useDeleteCompany,
  useGetCommonProfile,
  useLinkCompanyToProfile,
  useUpdateCompany,
} from 'services/hooks/companies';
import { useGetCompanyInfoById } from 'services/hooks/company';
import { useCreateFirmCompany } from 'services/hooks/firms';
import { useUserUtils } from 'utilities/userUtils';
import { NewCompanyDialogProps } from './NewCompanyDialog.props';

const ContentComponent = (itemName: string) => <ConfirmationDelete itemName={itemName}/>;

const NewCompanyDialog = ({
  open,
  onClose,
  editMode = false,
  isRelatedEntity = false,
  onSave
}: NewCompanyDialogProps) => {
  const {createCompany} = useCreateFirmCompany();
  const [{companyInfo, firmId}, dispatch] = useStore();
  const {isStaffUser, isSuperUser} = useUserUtils();
  const showProfilePicker = editMode && !isRelatedEntity && (isStaffUser() || isSuperUser());
  const [pendingProfileId, setPendingProfileId] = useState<number | null | undefined>(
    () => companyInfo.common_profile_id as number | null | undefined
  );
  const showProfileConfirm = editMode && !isRelatedEntity
        && pendingProfileId != null
        && (companyInfo.common_profile_confirmed as boolean | null | undefined) === false;
  const linkMutation = useLinkCompanyToProfile();
  const getCompanyInfo = useGetCompanyInfoById();
  const {data: pendingProfile, isLoading: pendingProfileLoading} = useGetCommonProfile(
    showProfileConfirm ? pendingProfileId as number : null,
  );
  const confirmMutation = useConfirmCompanyProfile();
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const defaultValues = editMode
    ? companyInfo
    : {
      name: '',
      legal_company_name: '',
      website: '',
      fiscal_year_end: '',
      captable_currency: 'USD',
      financials_currency: 'USD',
      // Leave IRR currency unset on create. Backend resolves NULL to
      // captable_currency via Company.effective_irr_currency; the user can
      // override later from the company settings.
      irr_currency: '',
    };
  const navigate = useNavigate();
  const {firmSlugParam} = useParams() as { firmSlugParam: string };
  const {showDialog} = useContext(DialogContext);
  const [formState, setFormState] = useState({
    isValid: editMode,
    values: defaultValues,
    errors: {},
  });

  const isEmptyCompany = isEmpty(pickBy(formState.values));

  const {deleteCompany: deleteCompanyMutation} = useDeleteCompany();
  const {updateCompany} = useUpdateCompany();
  const allowDeleteCompany = useMemo(
    () => editMode && !isEmpty(companyInfo) && !companyInfo?.measurement_date_id,
    [companyInfo, editMode]
  );

  const navigateToCompany = useCallback(
    (companySlug?: string | null) => {
      if (!firmSlugParam || !companySlug) {
        return;
      }

      dispatch(captableAction.setCaptableInfo({}));
      dispatch(allocationsAction.setAllocationInfo({}));
      navigate(companySummaryUrl(firmSlugParam, companySlug));
    },
    [dispatch, firmSlugParam, navigate]
  );

  const deleteCompany = async () => {
    const isDeleted = await deleteCompanyMutation({
      companyId: companyInfo.id,
      firmId: companyInfo.firm_id,
    });

    if (!isDeleted) {
      throw new Error('The company deletion failed');
    }

    onClose();
    navigate({pathname: firmSummaryUrl(firmSlugParam)});
  };

  const handleDeleteCompany = () => {
    showDialog({
      wrapper: ConfirmationDialog,
      content: () => ContentComponent(`Company: ${companyInfo.name}`),
      actions: [
        {
          label: 'Delete',
          variant: 'contained',
          type: 'danger',
          callback: deleteCompany,
        },
      ],
    });
  };

  const saveData = async () => {
    if (editMode) {
      const {
        firm_id,
        name,
        legal_company_name,
        website,
        fiscal_year_end,
        captable_currency,
        financials_currency,
        irr_currency,
      } = formState.values;

      const companyFormValues = {
        firm: firm_id,
        name,
        legal_company_name,
        website,
        fiscal_year_end,
        captable_currency,
        financials_currency,
        irr_currency,
      };

      // We must await the update so the Dialog knows to show the loading state
      const updateResult = await updateCompany({
        companyData: companyFormValues,
        companyId: companyInfo.id,
      });

      // If updateCompany follows the same pattern of returning null/undefined on error,
      // we throw here to prevent the dialog from closing.
      if (!updateResult) {
        throw new Error('The company update failed');
      }

      // Update common profile link if it changed
      if (showProfilePicker) {
        const currentProfileId = companyInfo.common_profile_id as number | null | undefined;
        if (pendingProfileId !== currentProfileId) {
          await linkMutation.mutateAsync({companyId: companyInfo.id, profileId: pendingProfileId ?? null});
          await getCompanyInfo(companyInfo.id as number);
        }
      }

      if (showProfileConfirm && pendingConfirm) {
        await confirmMutation.mutateAsync({companyId: companyInfo.id as number});
        await getCompanyInfo(companyInfo.id as number);
      }

      return updateResult;
    }

    const {values: companyData} = formState;

    const createPayload = isRelatedEntity
      ? {
        ...companyData,
        type: COMPANY_TYPE.HOLDING,
        parent_company: companyInfo.id,
        firm: firmId,
      }
      : {
        ...companyData,
        type: COMPANY_TYPE.PORTFOLIO,
        firm: firmId,
      };

    const newCompany = await createCompany(createPayload);

    // Since the createCompany hook swallows the error and returns null,
    // we manually throw to signal failure to the form dialog.
    if (!newCompany) {
      throw new Error('The company creation failed');
    }

    // For related entities, stay on the current page and let the parent handle refresh
    if (isRelatedEntity && onSave) {
      onSave(newCompany.name);
      // Manual onClose() removed; the form dialog handles this on successful resolution.
    } else {
      navigateToCompany(newCompany.slug);
    }

    return newCompany;
  };

  const getTitle = useCallback(() => {
    if (isRelatedEntity) {
      return RELATED_ENTITY.TITLE;
    }
    if (editMode) {
      return companyInfo.name;
    }
    return 'Add Company';
  }, [isRelatedEntity, editMode, companyInfo]);

  // Reset common-profile confirmation state every time the dialog is reopened.
  useEffect(() => {
    if (open) {
      setPendingProfileId(companyInfo.common_profile_id as number | null | undefined);
      setPendingConfirm(false);
    }
  }, [companyInfo.common_profile_id, open]);

  // Clearing the selected profile also clears the pending confirmation choice.
  useEffect(() => {
    if (pendingProfileId == null) {
      setPendingConfirm(false);
    }
  }, [pendingProfileId]);

  const shouldCheckFeatureEnabler = useMemo(() => !allowDeleteCompany, [allowDeleteCompany]);
  const handleDialogClose = useCallback(
    (updatedCompany?: unknown) => onClose(updatedCompany as Company | null | undefined),
    [onClose]
  );

  return (
    <FormDialog
      open={open}
      title={getTitle()}
      editMode={editMode}
      subtitle={isRelatedEntity ? RELATED_ENTITY.SUBTITLE : ''}
      onSave={saveData}
      onClose={handleDialogClose}
      onDelete={handleDeleteCompany}
      allowDelete={allowDeleteCompany}
      isValid={formState.isValid}
      confirmCancel={!isEmptyCompany}
      deleteText="Delete Company"
      toolTipText={COMPANIES_TOOLTIP_TEXT_COMPANY_MEASUREMENT_DATE}
      shouldCheckFeatureEnabler={shouldCheckFeatureEnabler}>
      <>
        <CompanyForm
          editMode={editMode}
          initialValues={defaultValues}
          defaultFormState={formState as unknown as CompanyFormDefaultFormState}
          onFormChange={formCurrentState => setFormState(formCurrentState)}
          isRelatedEntity={isRelatedEntity}
        />
        {showProfilePicker && (
          <CommonProfilePicker
            selectedProfileId={pendingProfileId}
            onChange={setPendingProfileId}
            disabled={linkMutation.isPending}
          />
        )}
        {showProfileConfirm && (
          <Box>
            <Divider sx={{my: 2}}/>
            <Typography variant="subtitle2" sx={{mb: 1}}>Common Profile</Typography>
            {pendingProfileLoading ? (
              <CircularProgress size={20}/>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  borderLeft: `3px solid ${AI_GRADIENT_COLORS.purple}`,
                  background: 'linear-gradient(90deg, rgba(166,20,255,0.06) 0%, transparent 100%)',
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <AutoAwesomeIcon sx={{
                    fontSize: 16,
                    background: AI_GRADIENT_CSS,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}/>
                  <Typography variant="body2">
                                        AI-suggested match:{' '}
                    <strong>{pendingProfile?.name ?? '—'}</strong>
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={pendingConfirm}
                      onChange={e => setPendingConfirm(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Confirm</Typography>}
                  sx={{mr: 0, ml: 0}}
                />
              </Paper>
            )}
          </Box>
        )}
      </>
    </FormDialog>
  );
};

export default NewCompanyDialog;
